/* src/components/Widgets.js - v5.91 - Server-Side Summary Fetch
   CHANGELOG (v5.9 -> v5.91): Widgets used to recompute
   everything (post/like counts, trending tags, friend activity, recent
   photos) by scanning the ENTIRE posts array on every render - only
   worked because App.js was downloading every post in the database on
   every load. Now that the main feed is paginated (App.js/routes/
   posts.py v2.13), this fetches its own aggregate from the new
   GET /api/widgets/summary instead of receiving `posts` as a prop at
   all - same 5 numbers/lists, just computed server-side once instead of
   re-scanned client-side on every render.
   CHANGELOG (v5.8 -> v5.9): Reported bug: the Trending widget's tags
   never seemed to change. Root cause confirmed against live data -
   tagCounts summed hashtag usage across ALL 1400+ posts ever made, with
   no time window at all, so the top 5 (#ai, #cybersecurity, #science,
   #gaming, #nasa) had such a large all-time lead that no genuinely
   recent tag could ever displace them, even though actual last-7-days
   usage looks meaningfully different. Now only counts posts from the
   last 7 days (post.id, not the mostly-missing post.timestamp field),
   so this behaves like an actual trending list instead of an all-time
   leaderboard. See also App.js's own fix for the separate bug where
   clicking a trending tag matched it as a loose substring anywhere in a
   post's text rather than as an actual hashtag. */
import React, { useState, useEffect } from 'react';

function Widgets({ user, onNavigatePost, onSearchTag, onNavigateGallery, onNavigate }) {

  const [summary, setSummary] = useState({
      post_count: 0, total_likes: 0, liked_count: 0,
      trending_tags: [], friend_activity: [], my_photos: []
  });

  useEffect(() => {
      if (!user) return;
      fetch(`/api/widgets/summary?current_user=${user.name}`)
          .then(res => res.ok ? res.json() : null)
          .then(data => { if (data) setSummary(data); })
          .catch(err => console.error('Widgets summary fetch error', err));
  }, [user]);

  if (!user) return null; // Widgets hidden for guests

  const trendingTags = summary.trending_tags;
  const friendActivity = summary.friend_activity;
  const myPhotos = summary.my_photos;

  // --- REUSABLE HEADER STYLE ---
  const headerStyle = {
      margin: '0 0 15px 0',
      borderBottom: '1px solid rgba(255,255,255,0.1)',
      paddingBottom: '10px'
  };

  return (
    <div className="widgets-container">
      
      {/* 1. GREETING SUMMARY */}
      <div className="card widget-greeting" style={{ padding: '20px', borderRadius: '15px' }}>
        <h3 style={headerStyle}>
            👋 Hello, {user.name}
        </h3>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: '#ccc' }}>
            <div style={{ textAlign: 'center' }}>
                <div style={{ fontWeight: 'bold', fontSize: '1.2rem', color: '#2ed573' }}>{summary.post_count}</div>
                <div>Posts</div>
            </div>
            <div style={{ textAlign: 'center' }}>
                <div style={{ fontWeight: 'bold', fontSize: '1.2rem', color: '#ff4757' }}>{summary.total_likes}</div>
                <div>Likes</div>
            </div>
            <div style={{ textAlign: 'center' }}>
                <div style={{ fontWeight: 'bold', fontSize: '1.2rem', color: '#1e90ff' }}>{summary.liked_count}</div>
                <div>Liked</div>
            </div>
        </div>
      </div>

      {/* 2. TRENDING WIDGET */}
      <div className="card widget-trending" style={{ padding: '20px', borderRadius: '15px' }}>
          <h3 style={headerStyle}>🔥 Trending</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {trendingTags.length > 0 ? trendingTags.map(tag => (
                  <span 
                    key={tag} 
                    onClick={() => onSearchTag(tag.replace('#', ''))}
                    style={{ background: 'rgba(255,255,255,0.1)', padding: '5px 10px', borderRadius: '15px', fontSize: '0.85rem', cursor: 'pointer' }}
                  >
                      {tag}
                  </span>
              )) : <div style={{ color: '#aaa' }}>No trends yet.</div>}
          </div>
      </div>

      {/* 3. FRIENDS ACTIVITY WIDGET */}
      <div className="card widget-friends" style={{ padding: '20px', borderRadius: '15px' }}>
          <h3 style={headerStyle}>👥 Friend Activity</h3>
          
          <div className="friend-list">
              {friendActivity.length > 0 ? friendActivity.map(p => (
                  <div 
                    key={p.id} 
                    className="friend-row"
                    onClick={() => onNavigatePost(p.id)} 
                  >
                      <div className="widget-friend-avatar" style={{ width: '35px', height: '35px', borderRadius: '50%', background: p.color || '#555', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '0.8rem', overflow:'hidden', flexShrink: 0 }}>
                          {p.userImage ? <img src={p.userImage} alt={p.username} style={{width:'100%', height:'100%', objectFit:'cover'}} /> : (p.username ? p.username[0].toUpperCase() : '?')}
                      </div>
                      
                      <div style={{ flex: 1, overflow: 'hidden' }}>
                          <div className="friend-name" style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>{p.username || 'Unknown User'}</div>
                          <div className="friend-msg" style={{ fontSize: '0.75rem', color: '#aaa', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {p.image ? '📷 Posted a photo' : p.message}
                          </div>
                      </div>
                      <div className="friend-time" style={{ fontSize: '0.7rem', color: '#666' }}>
                          {p.timestamp ? p.timestamp.split(' ')[0] : ''}
                      </div>
                  </div>
              )) : <div style={{ color: '#aaa', textAlign: 'center', fontSize: '0.9rem' }}>No recent activity from friends.</div>}
          </div>
      </div>

      {/* 4. USER'S GALLERY WIDGET */}
      <div className="card widget-gallery" style={{ padding: '20px', borderRadius: '15px' }}>
          <h3 style={headerStyle}>🖼️ My Photos</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '5px' }}>
              {myPhotos.map(p => (
                  <div 
                    key={p.id} 
                    onClick={() => onNavigateGallery(user.name, p.id)}
                    style={{ aspectRatio: '1/1', background: `url(${p.image}) center/cover`, borderRadius: '8px', cursor: 'pointer' }} 
                  />
              ))}
              {myPhotos.length === 0 && <div style={{ color: '#aaa', gridColumn: 'span 3', textAlign: 'center' }}>You haven't posted any photos.</div>}
          </div>
      </div>

      {/* 5. LEGAL LINKS (DESKTOP ONLY) */}
      <div className="desktop-only" style={{ display: 'flex', justifyContent: 'center', gap: '15px', fontSize: '0.8rem', color: '#a0a0b0' }}>
          <span onClick={() => { onNavigate('terms'); window.scrollTo(0,0); }} style={{ cursor: 'pointer', textDecoration: 'underline' }}>Terms of Service</span>
          <span>|</span>
          <span onClick={() => { onNavigate('privacy'); window.scrollTo(0,0); }} style={{ cursor: 'pointer', textDecoration: 'underline' }}>Privacy Policy</span>
      </div>

      {/* 6. FOOTER */}
      <div className="widget-footer" style={{ textAlign: 'center', fontSize: '0.8rem', color: '#666' }}>
          Nubix Social Network • Nubix AI • © 2026
      </div>

      {/* 7. RESPONSIVE STYLES */}
      <style>{`
        .widgets-container {
            display: flex;
            flex-direction: column;
            gap: 15px;
            
            /* --- NEW: Sticky & Scroll Logic --- */
            position: sticky;
            top: 20px; /* Stays 20px from top of view */
            max-height: calc(100vh - 40px); /* Never taller than viewport */
            overflow-y: auto; /* Scroll internally if needed */
            
            /* Hide scrollbar for cleaner look */
            scrollbar-width: none; 
        }
        .widgets-container::-webkit-scrollbar {
            display: none; 
        }

        .friend-list { display: flex; flex-direction: column; gap: 12px; }
        .friend-row { display: flex; gap: 10px; align-items: center; cursor: pointer; padding-bottom: 8px; border-bottom: 1px solid rgba(255,255,255,0.05); }

        /* --- NEW: Height-Based Hiding (Condense on Short Screens) --- */
        @media (max-height: 900px) {
            .widget-gallery { display: none; }
        }
        @media (max-height: 750px) {
            .widget-trending { display: none; }
            .widget-footer { display: none; }
        }

        /* Existing Width Responsiveness */
        @media (max-width: 1250px) {
            .widgets-container { gap: 8px !important; }
            .widget-friend-avatar { display: none !important; }
            .friend-list { gap: 0px !important; }
            .friend-row { padding-bottom: 3px !important; padding-top: 3px !important; gap: 5px !important; }
            .friend-name { font-size: 0.8rem !important; margin-bottom: 0px !important; line-height: 1.1 !important; }
            .friend-msg { font-size: 0.7rem !important; line-height: 1.1 !important; }
            .friend-time { font-size: 0.65rem !important; }
        }
      `}</style>

    </div>
  );
}

export default Widgets;
